import React, { useMemo } from 'react';
import { Box, Group, Paper, Text } from '@mantine/core';
import { yearColor } from './chartColors';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const W = 640;
const H = 220;
const PAD = { top: 16, right: 16, bottom: 28, left: 8 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

function buildPoints(values, max) {
  return values.map((value, index) => {
    const x = PAD.left + (index / 11) * PLOT_W;
    const y = PAD.top + PLOT_H - (max > 0 ? (value / max) * PLOT_H : 0);
    return { x, y, value, month: MONTHS[index] };
  });
}

function toPolyline(points) {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

/**
 * Courbes mensuelles multi-années (SVG).
 */
export default function MonthlyLineChart({
  title,
  subtitle,
  series = [],
  valueKey = 'amount',
  formatValue = (v) => String(v),
}) {
  const max = useMemo(() => {
    const vals = series.flatMap((s) =>
      (s.data || []).map((point) => Number(point[valueKey]) || 0),
    );
    return Math.max(1, ...vals);
  }, [series, valueKey]);

  const lines = useMemo(
    () =>
      series.map((entry, index) => ({
        ...entry,
        color: yearColor(index),
        points: buildPoints(
          (entry.data || []).map((point) => Number(point[valueKey]) || 0),
          max,
        ),
      })),
    [series, valueKey, max],
  );

  return (
    <Paper withBorder radius="lg" p="lg">
      <Group justify="space-between" mb="md" wrap="wrap">
        <div>
          <Text fw={600} size="sm">
            {title}
          </Text>
          {subtitle ? (
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          ) : null}
        </div>
        <Group gap="md">
          {lines.map((line) => (
            <Group gap={6} key={line.year}>
              <Box
                w={14}
                h={3}
                bg={`${line.color}.6`}
                style={{ borderRadius: 2 }}
              />
              <Text size="xs">{line.year}</Text>
            </Group>
          ))}
        </Group>
      </Group>

      <Box style={{ width: '100%', overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          role="img"
          aria-label={title}
          style={{ display: 'block', minWidth: 480 }}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = PAD.top + PLOT_H * (1 - ratio);
            return (
              <line
                key={ratio}
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y}
                y2={y}
                stroke="var(--mantine-color-gray-3)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            );
          })}

          {lines.map((line) => (
            <g key={line.year}>
              <polyline
                fill="none"
                stroke={`var(--mantine-color-${line.color}-6)`}
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={toPolyline(line.points)}
              />
              {line.points.map((point) => (
                <circle
                  key={`${line.year}-${point.month}`}
                  cx={point.x}
                  cy={point.y}
                  r="3.5"
                  fill={`var(--mantine-color-${line.color}-6)`}
                >
                  <title>
                    {line.year} — {point.month} : {formatValue(point.value)}
                  </title>
                </circle>
              ))}
            </g>
          ))}

          {MONTHS.map((label, index) => {
            const x = PAD.left + (index / 11) * PLOT_W;
            return (
              <text
                key={label}
                x={x}
                y={H - 8}
                textAnchor="middle"
                fontSize="10"
                fill="var(--mantine-color-dimmed)"
              >
                {label}
              </text>
            );
          })}
        </svg>
      </Box>
    </Paper>
  );
}
